import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CardCommentListModel,
  CardCommentModel,
  CreateCardCommentRequestModel,
  UpdateCardCommentRequestModel,
} from '../models/card-details-api.models';
import { asArray, asBoolean, asRecord, asString } from './card-api.utils';

@Injectable({ providedIn: 'root' })
export class CardCommentsApiService {
  private readonly http = inject(HttpClient);

  async getByCard(cardId: string): Promise<CardCommentListModel> {
    const response = await firstValueFrom(this.http.get<unknown>(`/api/cards/${cardId}/comments`));
    const items = asArray(response)
      .map((entry) => this.mapComment(entry, cardId))
      .filter((entry) => !!entry.id);
    return { items };
  }

  async create(cardId: string, payload: CreateCardCommentRequestModel): Promise<void> {
    await firstValueFrom(this.http.post(`/api/cards/${cardId}/comments`, payload));
  }

  async update(
    cardId: string,
    commentId: string,
    payload: UpdateCardCommentRequestModel
  ): Promise<void> {
    await firstValueFrom(this.http.put(`/api/cards/${cardId}/comments/${commentId}`, payload));
  }

  async delete(cardId: string, commentId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`/api/cards/${cardId}/comments/${commentId}`));
  }

  private mapComment(raw: unknown, cardId: string): CardCommentModel {
    const source = asRecord(raw);
    const nestedUser = asRecord(source['user'] ?? source['author'] ?? source['createdBy']);
    const nestedAvatar = nestedUser
      ? asString(
          nestedUser['avatarUrl'] ??
            nestedUser['AvatarUrl'] ??
            nestedUser['photoUrl'] ??
            nestedUser['imageUrl'] ??
            ''
        ).trim()
      : '';
    const flatAvatar =
      asString(
        source['userAvatarUrl'] ??
          source['avatarUrl'] ??
          source['AvatarUrl'] ??
          source['userImageUrl'] ??
          ''
      ).trim() || nestedAvatar;
    return {
      id: asString(source['id']),
      cardId: asString(source['cardId']) || cardId,
      userId: asString(source['userId']) || asString(nestedUser['id']),
      content: asString(source['content']) || asString(source['text']) || asString(source['body']),
      isEdited: asBoolean(source['isEdited']),
      isExternalReply: asBoolean(source['isExternalReply']),
      createdAt: asString(source['createdAt']),
      updatedAt: asString(source['updatedAt']) || asString(source['createdAt']),
      userAvatarUrl: flatAvatar || null,
    };
  }
}
